import { z } from 'zod';
import { DecimalStringSchema, IsoDateSchema } from './primitives.js';

type DeepReadonly<Value> = Value extends readonly (infer Item)[]
  ? readonly DeepReadonly<Item>[]
  : Value extends object
    ? { readonly [Key in keyof Value]: DeepReadonly<Value[Key]> }
    : Value;

export const BankMovementKindSchema = z.enum(['ERC20_TRANSFER', 'NATIVE_TRANSFER']);
export type BankMovementKind = DeepReadonly<z.infer<typeof BankMovementKindSchema>>;

export const BankSourceFingerprintSchema = z
  .object({
    walletId: z.number().int().positive(),
    chainId: z.number().int().positive(),
    assetId: z.number().int().positive(),
    eventType: z.enum(['DEPOSIT', 'WITHDRAWAL']),
    movementKind: BankMovementKindSchema,
    externalAddress: z.string().regex(/^0x[0-9a-f]{40}$/),
  })
  .strict();
export type BankSourceFingerprint = DeepReadonly<z.infer<typeof BankSourceFingerprintSchema>>;

export const BankSourceEventRefSchema = z
  .object({
    rawTxId: z.number().int().positive(),
    logIndexRef: z.number().int(),
    txHash: z.string().min(1),
    occurredAt: z.number().int().nonnegative(),
    amount: DecimalStringSchema,
    symbol: z.string().min(1),
  })
  .strict();
export type BankSourceEventRef = DeepReadonly<z.infer<typeof BankSourceEventRefSchema>>;

export const BankSourceCandidateGroupSchema = z
  .object({
    fingerprint: BankSourceFingerprintSchema,
    events: z.array(BankSourceEventRefSchema).min(1).max(1000),
  })
  .strict();
export type BankSourceCandidateGroup = DeepReadonly<z.infer<typeof BankSourceCandidateGroupSchema>>;

export const Held522CandidateSchema = z
  .object({
    assetId: z.number().int().positive(),
    symbol: z.string().min(1),
    chainId: z.number().int().positive(),
    chainName: z.string().min(1),
    heldQuantity: DecimalStringSchema,
    accountingAssetId: z.number().int().positive().nullable(),
  })
  .strict();
export type Held522Candidate = DeepReadonly<z.infer<typeof Held522CandidateSchema>>;

export const ConfirmBankSourcesRequestSchema = z
  .object({
    groups: z.array(BankSourceCandidateGroupSchema).min(1).max(100),
    reason: z.string().trim().min(10).max(500),
  })
  .strict();
export type ConfirmBankSourcesRequest = DeepReadonly<
  z.infer<typeof ConfirmBankSourcesRequestSchema>
>;

export const ConfirmHeld522RequestSchema = z
  .object({
    assetIds: z
      .array(z.number().int().positive())
      .min(1)
      .max(500)
      .refine((ids) => new Set(ids).size === ids.length),
    reason: z.string().trim().min(10).max(500),
  })
  .strict();
export type ConfirmHeld522Request = DeepReadonly<z.infer<typeof ConfirmHeld522RequestSchema>>;

export const ClosePreparationStepCodeSchema = z.enum([
  'SYNC',
  'CLASSIFICATION',
  'PRICING',
  'RECONCILIATION',
  'ENTRIES',
  'INTEGRITY',
]);
export type ClosePreparationStepCode = DeepReadonly<z.infer<typeof ClosePreparationStepCodeSchema>>;

export const ClosePreparationStepStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'SUCCESS',
  'BLOCKED',
  'FAILED',
]);
export type ClosePreparationStepStatus = DeepReadonly<
  z.infer<typeof ClosePreparationStepStatusSchema>
>;

export const ClosePreparationStatusSchema = z.enum([
  'IDLE',
  'RUNNING',
  'DECISIONS_REQUIRED',
  'BLOCKED',
  'READY',
  'FAILED',
]);
export type ClosePreparationStatus = DeepReadonly<z.infer<typeof ClosePreparationStatusSchema>>;

export const ClosePreparationStepSchema = z
  .object({
    code: ClosePreparationStepCodeSchema,
    status: ClosePreparationStepStatusSchema,
    message: z.string().min(1).max(500),
  })
  .strict();
export type ClosePreparationStep = DeepReadonly<z.infer<typeof ClosePreparationStepSchema>>;

export const CloseBlockerCodeSchema = z.enum([
  'UNKNOWN_EVENT',
  'UNCLASSIFIED_RAW',
  'EXCLUDED_ASSET',
  'MISSING_PRICE',
  'MISSING_CLOSING_PRICE',
  'FUTURE_PERIOD_END',
  'CUMP_UNRECONSTRUCTABLE',
  'UNMATCHED_BRIDGE',
  'LIAISON_RESIDUAL',
  'SUSPENSE_RESIDUAL',
  'ASSET_UNREVIEWED',
  'ASSET_REQUIRES_EC',
  'UNSUPPORTED_STAKING',
  'SPAM_UNREVIEWED',
  'RPC_MISSING',
  'RECONCILIATION_MISSING',
  'RECONCILIATION_FAILED',
  'RECONCILIATION_STALE',
  'RECONCILIATION_DIVERGENT',
]);
export type CloseBlockerCode = DeepReadonly<z.infer<typeof CloseBlockerCodeSchema>>;

export const CloseBlockerSchema = z
  .object({
    code: CloseBlockerCodeSchema,
    message: z.string().min(1).max(500),
    rawTxId: z.number().int().positive().optional(),
    assetId: z.number().int().positive().optional(),
    accountNum: z.string().min(1).optional(),
    walletId: z.number().int().positive().optional(),
    chainId: z.number().int().positive().optional(),
    timestamp: z.number().int().nonnegative().optional(),
    date: IsoDateSchema.optional(),
    symbol: z.string().min(1).optional(),
    balance: DecimalStringSchema.optional(),
  })
  .strict();
export type CloseBlocker = DeepReadonly<z.infer<typeof CloseBlockerSchema>>;

export const CloseBlockedErrorResponseSchema = z
  .object({
    error: z.literal('CloseBlockedError'),
    message: z.string().min(1),
    blockers: z.array(CloseBlockerSchema).min(1).max(10_000),
  })
  .strict();
export type CloseBlockedErrorResponse = DeepReadonly<
  z.infer<typeof CloseBlockedErrorResponseSchema>
>;

const CLOSE_PREPARATION_STEP_ORDER = [
  'SYNC',
  'CLASSIFICATION',
  'PRICING',
  'RECONCILIATION',
  'ENTRIES',
  'INTEGRITY',
] as const;

const ClosePreparationStepsSchema = z
  .array(ClosePreparationStepSchema)
  .length(CLOSE_PREPARATION_STEP_ORDER.length)
  .superRefine((steps, context) => {
    for (const [index, expected] of CLOSE_PREPARATION_STEP_ORDER.entries()) {
      if (steps[index]?.code !== expected) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'code'],
          message: `Expected preparation step ${expected}`,
        });
      }
    }
  });

export const ClosePreparationResultSchema = z
  .object({
    fiscalPeriodId: z.number().int().positive(),
    status: ClosePreparationStatusSchema,
    steps: ClosePreparationStepsSchema,
    remainingWork: z.number().int().nonnegative(),
    assetCandidates: z.array(Held522CandidateSchema).max(500),
    bankSourceCandidates: z.array(BankSourceCandidateGroupSchema).max(100),
    blockers: z.array(CloseBlockerSchema).max(10_000),
    warnings: z.array(z.string().min(1).max(500)).max(1000),
  })
  .strict();
export type ClosePreparationResult = DeepReadonly<z.infer<typeof ClosePreparationResultSchema>>;

export const BankSourceDecisionResultSchema = z
  .object({
    fiscalPeriodId: z.number().int().positive(),
    ruleIds: z.array(z.number().int().positive()).max(100),
    rawTxIds: z.array(z.number().int().positive()).max(100_000),
  })
  .strict();
export type BankSourceDecisionResult = DeepReadonly<z.infer<typeof BankSourceDecisionResultSchema>>;

export const Held522DecisionResultSchema = z
  .object({
    fiscalPeriodId: z.number().int().positive(),
    updatedAssetIds: z.array(z.number().int().positive()).max(500),
  })
  .strict();
export type Held522DecisionResult = DeepReadonly<z.infer<typeof Held522DecisionResultSchema>>;
